import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Check, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatDisplayDate } from "@/lib/dateFormat";
import { AutoAcceptEmisorPromptDialog } from "@/pages/invoices/AutoAcceptEmisorPromptDialog";
import { RecibidaRejectDialog } from "@/pages/invoices/RecibidaRejectDialog";
import {
  acceptRecibida,
  rejectRecibida,
  type RecibidaDetail,
} from "@/services/invoiceService";

const DGI_DECISION_WINDOW_DAYS = 20;

interface Props {
  detail: RecibidaDetail;
  t: TFunction;
  i18nLanguage: string;
}

/**
 * Surfaces the receptor's accept/reject decision on a received CFE
 * (DGI §XVIII). Three states:
 *
 *   - Decided (myAckCfeEstado set): show the green/red badge + when +
 *     whether Fiscalo auto-accepted or a user decided; for rejections
 *     also list the motivos sent to the emisor.
 *
 *   - Pending (eligible, no decision yet): show "Aceptar" + "Rechazar"
 *     buttons + a 20-día countdown so users see the urgency. Rejection
 *     opens a dialog where they pick a motivo and add detail.
 *
 *   - Not eligible (origin = sync / upload): hide entirely.
 */
export function RecibidaAckCfeCard({ detail, t, i18nLanguage }: Props): JSX.Element | null {
  const qc = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  // Populated by the accept-mutation success handler when the backend
  // signals "first time accepting from this emisor" — drives the
  // post-accept "¿siempre aceptar de este emisor?" dialog.
  const [autoAcceptPrompt, setAutoAcceptPrompt] = useState<
    { rucEmisor: string; legalName: string | null } | null
  >(null);

  const accept = useMutation({
    mutationFn: () => acceptRecibida(detail.id),
    onSuccess: (res) => {
      toast.success(
        t("recibidas.ackCfeAcceptedToast", "Aceptada — avisando al emisor"),
      );
      qc.invalidateQueries({ queryKey: ["recibida", detail.id] });
      if (res.promptAutoAccept) setAutoAcceptPrompt(res.promptAutoAccept);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Error");
    },
  });

  const reject = useMutation({
    mutationFn: (motivos: Array<{ code: string; description: string; detail?: string }>) =>
      rejectRecibida(detail.id, motivos),
    onSuccess: () => {
      toast.success(
        t("recibidas.ackCfeRejectedToast", "Rechazada — avisando al emisor"),
      );
      setRejectOpen(false);
      qc.invalidateQueries({ queryKey: ["recibida", detail.id] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Error");
    },
  });

  const isEligible = detail.isEligibleForAckCfe !== false && !!detail.envioCfeMessageId;
  const estado = detail.myAckCfeEstado;
  const motivos = detail.myAckCfeMotivos ?? [];

  // Day count + countdown calc.
  const { daysElapsed, daysRemaining } = useMemo(() => {
    if (!detail.createdAt) return { daysElapsed: 0, daysRemaining: DGI_DECISION_WINDOW_DAYS };
    const created = new Date(detail.createdAt).getTime();
    const elapsed = Math.floor((Date.now() - created) / (1000 * 60 * 60 * 24));
    return {
      daysElapsed: Math.max(0, elapsed),
      daysRemaining: Math.max(0, DGI_DECISION_WINDOW_DAYS - elapsed),
    };
  }, [detail.createdAt]);

  if (!isEligible) return null;

  return (
    <Card className="shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-sm">
      <CardHeader>
        <CardTitle className="text-muted-foreground text-sm font-medium">
          {t("recibidas.ackCfeTitle", "Aceptación / rechazo del comprobante")}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-3">
        {estado === "AE" ? (
          <div className="space-y-1">
            <StatusBadge variant="success">
              {t(
                detail.myAckCfeDecidedByUserId
                  ? "recibidas.ackCfeAcceptedByUser"
                  : "recibidas.ackCfeAcceptedAuto",
                detail.myAckCfeDecidedByUserId
                  ? "Aceptada el {{date}}"
                  : "Aceptada por Fiscalo el {{date}} (auto, día 19)",
                {
                  date: detail.myAckCfeDecidedAt
                    ? formatDisplayDate(detail.myAckCfeDecidedAt, { locale: i18nLanguage })
                    : "—",
                },
              )}
            </StatusBadge>
            {detail.myAckCfeSentAt ? (
              <p className="text-xs text-muted-foreground">
                {t("recibidas.ackCfeSent", "Avisamos al emisor el {{date}}", {
                  date: formatDisplayDate(detail.myAckCfeSentAt, { locale: i18nLanguage }),
                })}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("recibidas.ackCfeSendingToEmisor", "Avisando al emisor…")}
              </p>
            )}
          </div>
        ) : estado === "RE" ? (
          <div className="space-y-2">
            <StatusBadge variant="danger">
              {t("recibidas.ackCfeRejected", "Rechazada el {{date}}", {
                date: detail.myAckCfeDecidedAt
                  ? formatDisplayDate(detail.myAckCfeDecidedAt, { locale: i18nLanguage })
                  : "—",
              })}
            </StatusBadge>
            {motivos.length > 0 && (
              <ul className="ml-1 text-xs text-muted-foreground space-y-0.5">
                {motivos.map((m, i) => (
                  <li key={i}>
                    <code className="text-[10px]">{m.code}</code> — {m.description}
                    {m.detail ? <span className="text-muted-foreground/70"> ({m.detail})</span> : null}
                  </li>
                ))}
              </ul>
            )}
            {detail.myAckCfeSentAt ? (
              <p className="text-xs text-muted-foreground">
                {t("recibidas.ackCfeSent", "Avisamos al emisor el {{date}}", {
                  date: formatDisplayDate(detail.myAckCfeSentAt, { locale: i18nLanguage }),
                })}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("recibidas.ackCfeSendingToEmisor", "Avisando al emisor…")}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-muted-foreground">
              {t(
                "recibidas.ackCfeExplain",
                "DGI requiere que aceptes o rechaces este comprobante dentro de los 20 días (§XVIII).",
              )}{" "}
              {daysRemaining > 0 ? (
                <span className={daysRemaining <= 3 ? "text-[var(--fx-warn-text)] font-medium" : ""}>
                  {t("recibidas.ackCfeCountdown", "Quedan {{n}} días.", { n: daysRemaining })}
                </span>
              ) : (
                <span className="text-[var(--fx-warn-text)] font-medium">
                  {t("recibidas.ackCfeOverdue", "Plazo vencido — Fiscalo aceptará automáticamente si no decidís.")}
                </span>
              )}
              {daysElapsed > 0 ? (
                <span className="ml-1 text-xs">
                  ({t("recibidas.ackCfeReceivedDaysAgo", "recibido hace {{n}} días", { n: daysElapsed })})
                </span>
              ) : null}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => accept.mutate()}
                disabled={accept.isPending || reject.isPending}
              >
                <Check className="h-3.5 w-3.5" />
                {t("recibidas.ackCfeAccept", "Aceptar")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setRejectOpen(true)}
                disabled={accept.isPending || reject.isPending}
              >
                <X className="h-3.5 w-3.5" />
                {t("recibidas.ackCfeReject", "Rechazar")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <RecibidaRejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onSubmit={(motivos) => reject.mutate(motivos)}
        submitting={reject.isPending}
        t={t}
      />
      <AutoAcceptEmisorPromptDialog
        prompt={autoAcceptPrompt}
        onClose={() => setAutoAcceptPrompt(null)}
      />
    </Card>
  );
}

